import { Directive, Input, booleanAttribute, computed, signal } from '@angular/core';
import { hlm } from '@spartan-ng/helm/utils';
import { BrnMenuItemDirective } from '@spartan-ng/ui-menu-brain';
import { type VariantProps, cva } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const hlmMenuItemVariants = cva(
  'group w-full relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
  {
    variants: { inset: { true: 'pl-8', false: '' } },
    defaultVariants: { inset: false },
  },
);
export type HlmMenuItemVariants = VariantProps<typeof hlmMenuItemVariants>;

@Directive({
  selector: '[hlmMenuItem]',
  standalone: true,
  host: {
    '[class]': '_computedClass()',
  },
  hostDirectives: [
    {
      directive: BrnMenuItemDirective,
      inputs: ['disabled: disabled'],
      outputs: ['triggered: triggered'],
    },
  ],
})
export class HlmMenuItemDirective {
  // internal writable signal to hold user-provided classes
  private readonly userCls = signal<ClassValue>('');

  // Non-aliased compatibility input: prefer binding to [userClass]
  @Input()
  set userClass(cls: ClassValue) {
    this.userCls.set(cls ?? '');
  }

  // Keep backwards compatibility: allow templates to bind [class]="..."
  @Input()
  set class(cls: ClassValue) {
    this.userCls.set(cls);
  }

  private readonly _inset = signal<boolean>(false);

  protected _computedClass = computed(() =>
    hlm(hlmMenuItemVariants({ inset: this._inset() }), this.userCls()),
  );

  @Input({ transform: booleanAttribute })
  set inset(value: boolean) {
    this._inset.set(value);
  }
}
